import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnInit,
  output,
  Renderer2,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, fromEvent, map, switchMap, takeLast, takeUntil, tap } from 'rxjs';

import { mouseRxUtils } from '@blaze-shared/shared-util';

const MIN_COLUMN_WIDTH_PX = 64;
const RESIZING_CLASSNAME = 'resizing';
const CONTAINER_CLASSNAME = 'category-container';
const COLUMN_WIDTH_VAR = '--b-gc-header-width';

@UntilDestroy()
@Component({
  selector: 'be-gantt-chart-resize-column',
  template: '',
  styleUrls: ['./gantt-chart-resize-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GanttChartResizeColumnComponent implements OnInit {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  resizeColumn = output<number>();

  resizer: HTMLElement;
  cells: HTMLElement[] = [];

  constructor() {
    this.resizer = this.el.nativeElement;
  }

  ngOnInit(): void {
    const cell: HTMLElement = this.renderer.parentNode(this.resizer);
    const row: HTMLElement = this.renderer.parentNode(cell);
    const rowParent: HTMLElement = this.renderer.parentNode(row);
    const table: HTMLElement = rowParent.classList.contains(CONTAINER_CLASSNAME)
      ? this.renderer.parentNode(rowParent)
      : rowParent;

    if (!cell || !row || !table) {
      throw new Error(`Can't access parent table element`);
    }

    fromEvent<MouseEvent>(this.resizer, 'mousedown')
      .pipe(
        untilDestroyed(this),
        tap((event) => {
          event.preventDefault();
          event.stopPropagation();
          this.renderer.addClass(table, RESIZING_CLASSNAME);
        }),
        map((event) => ({
          startX: event.pageX,
          columnWidth: cell.offsetWidth,
        })),
        switchMap((config) =>
          fromEvent<MouseEvent>(document, 'mousemove').pipe(
            map((moveEvent) => {
              const width = Math.max(
                config.columnWidth + (moveEvent.pageX - config.startX),
                MIN_COLUMN_WIDTH_PX,
              );
              table.style.setProperty(COLUMN_WIDTH_VAR, `${width}px`);
              return width;
            }),
            takeUntil(mouseRxUtils.buttonRelease()),
            takeLast(1),
            finalize(() => {
              this.renderer.removeClass(table, RESIZING_CLASSNAME);
            }),
          ),
        ),
      )
      .subscribe((value) => this.resizeColumn.emit(value));
  }
}
